import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, DownloadSimple, FileXls, UploadSimple } from "@phosphor-icons/react";

export default function ItemImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/inventory/items/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "item-import-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    setResult(null);
    setFile(f);
  };

  const runImport = async () => {
    if (!file) return;
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/inventory/items/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} item${data.created === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
        onImported?.();
      } else {
        toast.info(`No items imported (${data.skipped} skipped)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="item-import-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">Inventory</div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Import Items from Excel</h2>
          </div>
          <button type="button" onClick={handleClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded" data-testid="item-import-dialog-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm text-slate-700 font-medium">1. Get the template</p>
            <p className="text-xs text-slate-500 mt-1">
              Download the .xlsx template with all the columns Item Master accepts (name is required; the rest are optional).
            </p>
            <button
              type="button"
              onClick={downloadTemplate}
              disabled={downloading}
              className="mt-3 inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60"
              data-testid="item-import-download-template"
            >
              <DownloadSimple size={14} weight="bold" />
              {downloading ? "Preparing…" : "Download Template"}
            </button>
          </div>

          <div>
            <p className="text-sm text-slate-700 font-medium">2. Upload your filled-in file</p>
            <p className="text-xs text-slate-500 mt-1">
              Rows with a name that already exists in Item Master are skipped, not overwritten.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="item-import-file"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-3 w-full flex items-center gap-3 h-11 px-4 rounded-md border border-dashed border-slate-300 bg-white text-sm text-slate-600 hover:bg-slate-50"
              data-testid="item-import-file-btn"
            >
              <FileXls size={18} className="text-emerald-600 shrink-0" />
              <span className="truncate">{file ? file.name : "Choose .xlsx file…"}</span>
            </button>
          </div>

          {result && (
            <div
              className={`rounded-md border px-4 py-3 text-sm ${
                result.created > 0 ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
              data-testid="item-import-result"
            >
              <p className="font-medium">
                {result.created} created · {result.skipped} skipped
              </p>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <ul className="mt-2 space-y-0.5 max-h-28 overflow-y-auto text-xs opacity-80">
                  {result.errors.slice(0, 20).map((err, i) => (
                    <li key={i}>Row {err.row}: {err.reason}</li>
                  ))}
                  {result.errors.length > 20 && <li>…and {result.errors.length - 20} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={handleClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50" data-testid="item-import-cancel">
            {result ? "Close" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={runImport}
            disabled={!file || importing}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="item-import-submit"
          >
            <UploadSimple size={14} weight="bold" />
            {importing ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}
